import Vue from 'vue';
import { removeReflectionDocument, createReflectionDocument } from './reflectionDocumentHelper';
import { FIRESTORE_COLLECTION_REFLECTIONS } from '../constants/backendConstants';
import { types as analysesTypes } from './analyses';
import { types as bibliographyTypes } from './bibliography';
import { types as crudTypes } from './crud';

const MODULE_PREFIX = 'aclEditor';

const GETTER_ACL = 'acl';
const GETTER_ACL_ADDED = 'aclAdded';
const GETTER_ACL_REMOVED = 'aclRemoved';
const GETTER_LAST_ACL_CHANGED_ENTITY_ID = 'lastAclChangedEntityId';

const GETTER_LINKED_REFLECTIONS = 'linkedReflections';
const GETTER_UNLINKED_REFLECTIONS = 'unlinkedReflections';
const GETTER_REFLECTIONS_TO_LINK = 'reflectionsLinked';
const GETTER_REFLECTIONS_TO_UNLINK = 'reflectionsUnlinked';

const MUTATION_ACL = 'MUTATION_ACL';
const MUTATION_ACL_ADDED = 'MUTATION_ACL_ADDED';
const MUTATION_ACL_REMOVED = 'MUTATION_ACL_REMOVED';
const MUTATION_LAST_ACL_CHANGED_ENTITY_ID = 'MUTATION_LAST_ACL_CHANGED_ENTITY_ID';

const MUTATION_SET_ENTITY_TYPE = 'setEntityType';
const MUTATION_UPDATE_LINKED_REFLECTIONS = 'updateLinkedReflections';
const MUTATION_UPDATE_UNLINKED_REFLECTIONS = 'updateUnlinkedReflections';
const MUTATION_UPDATE_REFLECTIONS_TO_LINK = 'updateReflectionsToLink';
const MUTATION_UPDATE_REFLECTIONS_TO_UNLINK = 'updateReflectionsToUnlink';

const ACTION_SAVE_CHANGES = 'saveChanges';
const ACTION_ADD_ACL = 'addAcl';
const ACTION_REMOVE_ACL = 'removeAcl';
const ACTION_INIT = 'init';

const ACTION_LINK_REFLECTION = 'linkReflection';
const ACTION_UNLINK_REFLECTION = 'unlinkReflection';

const INITIAL_STATE = {
  acl: [],
  lastAclChangedEntityId: undefined,
  aclAdded: new Map(),
  aclRemoved: new Map(),
  entityType: undefined,
  linkedReflections: [],
  unlinkedReflections: [],
  reflectionsToLink: [],
  reflectionsToUnlink: [],
};

const getters = {
  [GETTER_ACL]: (state) => state.acl,
  [GETTER_LAST_ACL_CHANGED_ENTITY_ID]: (state) => state.lastAclChangedEntityId,
  [GETTER_ACL_ADDED]: (state) => state.aclAdded,
  [GETTER_ACL_REMOVED]: (state) => state.aclRemoved,
  [GETTER_LINKED_REFLECTIONS]: (state) => state.linkedReflections,
  [GETTER_UNLINKED_REFLECTIONS]: (state) => state.unlinkedReflections,
  [GETTER_REFLECTIONS_TO_LINK]: (state) => state.reflectionsToLink,
  [GETTER_REFLECTIONS_TO_UNLINK]: (state) => state.reflectionsToUnlink,
};

const mutations = {
  [MUTATION_ACL]: (state, value) => { state.acl = value; },
  [MUTATION_ACL_ADDED]: (state, value) => { state.aclAdded = value; },
  [MUTATION_ACL_REMOVED]: (state, value) => { state.aclRemoved = value; },
  [MUTATION_LAST_ACL_CHANGED_ENTITY_ID]: (state, value) => { state.lastAclChangedEntityId = value; },
  [MUTATION_SET_ENTITY_TYPE]: (state, entityType) => { state.entityType = entityType; },
  [MUTATION_UPDATE_LINKED_REFLECTIONS]: (state, reflections) => { state.linkedReflections = reflections; },
  [MUTATION_UPDATE_UNLINKED_REFLECTIONS]: (state, reflections) => { state.unlinkedReflections = reflections; },
  [MUTATION_UPDATE_REFLECTIONS_TO_LINK]: (state, reflectionsToLink) => { state.reflectionsToLink = reflectionsToLink; },
  [MUTATION_UPDATE_REFLECTIONS_TO_UNLINK]: (state, reflectionsToUnlink) => { state.reflectionsToUnlink = reflectionsToUnlink; },
};

const saveAnalysis = async (analysis, commit, dispatch, state, acl) => {
  const portfolioIdsToAdd = Array.from(state.aclAdded.values()).map(accessor => accessor.entityId);
  const portfolioIdsToRemove = Array.from(state.aclRemoved.values()).map(accessor => accessor.entityId);

  commit(
    analysesTypes.mutations.FETCHED_ANALYSIS,
    { ...analysis },
    { root: true },
  );
  await dispatch(
    analysesTypes.actions.updateAnalysisAcl,
    { acl, portfolioIdsToAdd, portfolioIdsToRemove },
    { root: true },
  );
};

const saveReflection = async (reflection, dispatch, acl) => {
  const documentPath = `${FIRESTORE_COLLECTION_REFLECTIONS}/${reflection.id}`;

  await dispatch(
    crudTypes.actions.updateDocument,
    { documentPath, updateData: { acl } },
    { root: true },
  );

  await dispatch(
    'reflections/validateSupportingDocsACL',
    { acl },
    { root: true },
  );
};

const actions = {
  [ACTION_SAVE_CHANGES]: async ({ commit, dispatch, state }, document) => {
    const acl = state.acl
      .filter(accessor => !state.aclRemoved.has(accessor.entityId))
      .concat(Array.from(state.aclAdded.values()));

    if (state.entityType === 'analysis') {
      await saveAnalysis(document, commit, dispatch, state, acl);

      const addedPromises = state.reflectionsToLink.map(reflection => createReflectionDocument(document.id, reflection.id));
      const removedPromises = state.reflectionsToUnlink.map(reflection => removeReflectionDocument(document.id, reflection.id));
      await Promise.all(removedPromises.concat(addedPromises));
    }

    if (state.entityType === 'reflection') {
      await saveReflection(document, dispatch, acl);
    }

    commit(MUTATION_ACL, acl);
    commit(MUTATION_UPDATE_LINKED_REFLECTIONS, []);
    commit(MUTATION_UPDATE_UNLINKED_REFLECTIONS, []);

    commit(MUTATION_ACL_ADDED, new Map());
    commit(MUTATION_ACL_REMOVED, new Map());
    commit(MUTATION_UPDATE_REFLECTIONS_TO_LINK, []);
    commit(MUTATION_UPDATE_REFLECTIONS_TO_UNLINK, []);

    commit(MUTATION_LAST_ACL_CHANGED_ENTITY_ID, undefined);
    // Always re-trigger even when same doc
    await Vue.nextTick();
    commit(MUTATION_LAST_ACL_CHANGED_ENTITY_ID, document.id);
  },
  [ACTION_ADD_ACL]: async ({ state, commit }, acl) => {
    state.aclAdded.set(acl.entityId, acl);
    commit(MUTATION_ACL_ADDED, state.aclAdded);
  },
  [ACTION_INIT]: async ({ commit, dispatch, rootGetters }, { entityType, entityId, acl }) => {
    if (entityType === 'analysis') {
      await dispatch(bibliographyTypes.actions.fetchReflections, { }, { root: true });
      const reflectionsLinked = rootGetters[bibliographyTypes.getters.findReflectionsWithSupportingAnalysis](entityId);
      const reflectionsUnlinked = rootGetters[bibliographyTypes.getters.findReflectionsWithoutSupportingAnalysis](entityId);

      commit(MUTATION_UPDATE_LINKED_REFLECTIONS, reflectionsLinked);
      commit(MUTATION_UPDATE_UNLINKED_REFLECTIONS, reflectionsUnlinked);
    } else {
      commit(MUTATION_UPDATE_LINKED_REFLECTIONS, []);
      commit(MUTATION_UPDATE_UNLINKED_REFLECTIONS, []);
    }

    commit(MUTATION_SET_ENTITY_TYPE, entityType);
    commit(MUTATION_ACL, acl);

    commit(MUTATION_ACL_ADDED, new Map());
    commit(MUTATION_ACL_REMOVED, new Map());
    commit(MUTATION_UPDATE_REFLECTIONS_TO_LINK, []);
    commit(MUTATION_UPDATE_REFLECTIONS_TO_UNLINK, []);
  },
  [ACTION_REMOVE_ACL]: async ({ state, commit }, acl) => {
    if (state.aclAdded.has(acl.entityId)) {
      state.aclAdded.delete(acl.entityId);
      commit(MUTATION_ACL_ADDED, state.aclAdded);
      return;
    }

    if (state.aclRemoved.has(acl.entityId)) {
      state.aclRemoved.delete(acl.entityId);
      commit(MUTATION_ACL_REMOVED, state.aclRemoved);
      return;
    }

    const currentAcl = state.acl.find(a => a.entityId === acl.entityId);
    if (currentAcl) {
      state.aclRemoved.set(acl.entityId, acl);
      commit(MUTATION_ACL_REMOVED, state.aclRemoved);
    }
  },
  [ACTION_LINK_REFLECTION]: async ({ state, commit }, reflection) => {
    const isInToLink = !!state.reflectionsToLink.find(r => reflection.id === r.id);
    const isAlreadyLinked = !!state.linkedReflections.find(r => reflection.id === r.id);
    if (isInToLink || isAlreadyLinked) return;

    const reflectionsToLink = state.reflectionsToLink.concat([reflection]);
    commit(MUTATION_UPDATE_REFLECTIONS_TO_LINK, reflectionsToLink);
  },
  [ACTION_UNLINK_REFLECTION]: async ({ state, commit }, reflection) => {
    const isInToLink = !!state.reflectionsToLink.find(r => reflection.id === r.id);

    if (isInToLink) {
      const reflectionsToLink = state.reflectionsToLink.filter((r) => r.id !== reflection.id);
      commit(MUTATION_UPDATE_REFLECTIONS_TO_LINK, reflectionsToLink);
      return;
    }

    const isInToUnlink = !!state.reflectionsToUnlink.find(r => reflection.id === r.id);

    if (!isInToUnlink) {
      const reflectionsToUnlink = state.reflectionsToUnlink.concat([reflection]);
      commit(MUTATION_UPDATE_REFLECTIONS_TO_UNLINK, reflectionsToUnlink);
      return;
    }

    if (isInToUnlink) {
      const reflectionsToUnlink = state.reflectionsToUnlink.filter((r) => r.id !== reflection.id);
      commit(MUTATION_UPDATE_REFLECTIONS_TO_UNLINK, reflectionsToUnlink);
    }
  },
};

export default {
  namespaced: true,
  state: INITIAL_STATE,
  getters,
  mutations,
  actions,
};

export const types = {
  getters: {
    acl: `${MODULE_PREFIX}/${GETTER_ACL}`,
    aclAdded: `${MODULE_PREFIX}/${GETTER_ACL_ADDED}`,
    aclRemoved: `${MODULE_PREFIX}/${GETTER_ACL_REMOVED}`,
    linkedReflections: `${MODULE_PREFIX}/${GETTER_LINKED_REFLECTIONS}`,
    unlinkedReflections: `${MODULE_PREFIX}/${GETTER_UNLINKED_REFLECTIONS}`,
    reflectionsToLink: `${MODULE_PREFIX}/${GETTER_REFLECTIONS_TO_LINK}`,
    reflectionsToUnlink: `${MODULE_PREFIX}/${GETTER_REFLECTIONS_TO_UNLINK}`,
    lastAclChangedEntityId: `${MODULE_PREFIX}/${GETTER_LAST_ACL_CHANGED_ENTITY_ID}`,
  },
  mutations: {},
  actions: {
    init: `${MODULE_PREFIX}/${ACTION_INIT}`,
    saveChanges: `${MODULE_PREFIX}/${ACTION_SAVE_CHANGES}`,
    addAcl: `${MODULE_PREFIX}/${ACTION_ADD_ACL}`,
    removeAcl: `${MODULE_PREFIX}/${ACTION_REMOVE_ACL}`,
    linkReflection: `${MODULE_PREFIX}/${ACTION_LINK_REFLECTION}`,
    unlinkReflection: `${MODULE_PREFIX}/${ACTION_UNLINK_REFLECTION}`,
  },
};
